import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, RotateCcw } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { JourneyPaths } from "@/components/journey/JourneyPaths";
import { getJourneyProgress, getMemory } from "@/services/unsaidService";
import { prepareFinale } from "@/lib/journeyFinale";
import { BeginningMessage } from "@/components/journey/BeginningMessage";
import { PrimaryButton } from "@/components/fionova/Primitives";
import { unsaidStore } from "@/lib/unsaidStore";
import { onboardingState } from "@/lib/onboardingState";
import { claimGuestJourney, journey } from "@/lib/journey";
import { useAuth } from "@/context/AuthContext";
import { trackSessionOnce } from "@/lib/analytics";

const SECTIONS = [["heard", "What I've heard"], ["patterns", "Patterns we may be discovering"], ["matters", "What matters most to you"], ["next", "Where we can go from here"]];

// The journey finale: optional book, Ellie's four-part closing reflection, then the three ending pathways.
export default function OnboardingComplete() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [recap, setRecap] = useState(null);
  const [book, setBook] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const started = useRef(false);
  const [showPaths, setShowPaths] = useState(false);

  const run = async () => {
    setState("loading");
    const res = await prepareFinale();
    if (res.book) setBook(res.book);
    if (res.status === 409) { navigate(journey.lastStep() || "/the-unsaid", { replace: true }); return; }
    if (!res.recap) { setState("error"); return; }
    setRecap(res.recap);
    journey.off();
    onboardingState.completeOnboarding();
    trackSessionOnce("onboarding_completed", "onboarding_completed", { entry: "journey" });
    if (user) { await claimGuestJourney(); refresh(); }
    setState("ready");
  };

  useEffect(() => {
    if (started.current || user === undefined) return;
    started.current = true;
    if (user?.entitled && !journey.guestId() && !["active", "completed"].includes(user.journey_status)) { navigate("/app", { replace: true }); return; }
    (async () => {
      const p = await getJourneyProgress();
      if (p.status === "completed" && p.recap) {
        setRecap(p.recap);
        const mem = p.book ? { book: p.book } : user ? await getMemory().catch(() => ({})) : {};
        if (mem.book) setBook(mem.book);
        journey.off(); onboardingState.completeOnboarding();
        if (user && journey.guestId()) { await claimGuestJourney(); refresh(); }
        setState("ready"); return;
      }
      if (!journey.isOn() && !journey.guestId() && user?.journey_status !== "active") { navigate("/journey", { replace: true }); return; }
      journey.on();
      // Mid-journey visits (typed URL, back/forward) must not end the one free journey early.
      if (!unsaidStore.getPurposeOutcome()?.reflection) { navigate(journey.lastStep() || "/the-unsaid", { replace: true }); return; }
      run();
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state !== "ready") {
    return (
      <OnboardingLayout testId="page-journey-complete" contentClassName="items-center justify-center py-10 text-center">
        <EllieOrb size={72} testId="recap-loading-avatar" />
        {state === "loading" ? (
          <p className="mt-6 text-base text-slate-600" data-testid="recap-loading">Bringing everything we've explored together…</p>
        ) : (
          <div className="mt-6 flex flex-col items-center gap-4" data-testid="recap-error">
            <p className="max-w-sm text-base text-slate-600">Let me take another moment to gather this.</p>
            <button data-testid="recap-retry" onClick={run}
              className="inline-flex items-center gap-2 rounded-full bg-white/80 px-5 py-2.5 text-sm font-medium text-slate-800 ring-1 ring-slate-200 hover:ring-teal-400">
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          </div>
        )}
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout testId="page-journey-complete" contentClassName="py-12">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col items-center text-center">
        <EllieOrb size={88} testId="recap-avatar" />
        <p className="eyebrow mt-6 text-[11px] text-teal-700" data-testid="recap-eyebrow">All five parts complete · Your closing reflection</p>
        <h1 className="mt-3 text-[30px] font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
          Here's what I've <span className="fio-gradient-text">come to understand</span>
        </h1>
      </motion.div>

      {user?.partner_ended && !user?.entitled && (
        <p className="mx-auto mt-6 max-w-md rounded-2xl bg-white/80 px-4 py-3 text-center text-sm leading-relaxed text-slate-700 ring-1 ring-slate-200" data-testid="partner-ended-note">
          Your complimentary access has ended. Everything you've saved is still here — choose below how you'd like to continue.
        </p>
      )}
      {recap.crisis ? (
        <div className="fio-glass-strong mt-10 rounded-3xl p-6 ring-1 ring-rose-200" data-testid="recap-crisis">
          <p className="text-[15.5px] leading-relaxed text-slate-800">{recap.heard}</p>
          <p className="mt-3 text-sm text-slate-600">In the US, you can call or text <strong>988</strong> any time. If you're in immediate danger, please call your local emergency number.</p>
          {!user && (
            <button data-testid="recap-crisis-save" onClick={() => navigate("/begin?next=journey")} className="mt-4 text-sm font-medium text-teal-700 underline-offset-4 hover:underline">
              Whenever you're ready, a free account keeps everything we talked about.
            </button>
          )}
          {!showPaths && (
            <div className="mt-4"><button data-testid="recap-crisis-next" onClick={() => setShowPaths(true)} className="text-sm font-medium text-slate-600 underline-offset-4 hover:underline">
              When you feel ready, see where you can go from here
            </button></div>
          )}
          {showPaths && <JourneyPaths />}
        </div>
      ) : (
        <>
          <div className="mt-10 space-y-4">
            {SECTIONS.filter(([k]) => recap[k]).map(([k, label], i) => (
              <motion.div key={k} data-testid={`recap-${k}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 + i * 0.12 }}
                className="fio-glass fio-hairline rounded-3xl p-6">
                <p className="eyebrow text-[11px] text-teal-700">{label}</p>
                <p className="mt-2 text-[15.5px] leading-relaxed text-slate-800">{recap[k]}</p>
              </motion.div>
            ))}
          </div>
          {book && (
            <div className="fio-glass fio-hairline mt-4 rounded-3xl p-6" data-testid="book-recommendation">
              <div className="flex items-center gap-2 text-teal-700"><BookOpen className="h-4 w-4" /><p className="eyebrow text-[11px]">A book that may fit where you are</p></div>
              <h3 className="mt-3 text-lg font-semibold leading-snug text-slate-900" data-testid="book-title">{book.title}</h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-slate-700" data-testid="book-why">{book.why}</p>
              {book.link && <a data-testid="book-link" href={book.link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-200 hover:ring-teal-400">View on Amazon</a>}
            </div>
          )}
          {recap.closing && <p className="mx-auto mt-8 max-w-md text-center text-[16px] italic leading-relaxed text-slate-700" data-testid="recap-closing">{recap.closing}</p>}
          {user?.entitled ? (
            <div className="mx-auto mt-10 max-w-md"><PrimaryButton data-testid="recap-go-app" onClick={() => navigate("/app")}>Continue to my dashboard</PrimaryButton></div>
          ) : (<><BeginningMessage guest={!user} /><JourneyPaths /></>)}
        </>
      )}
    </OnboardingLayout>
  );
}
